/**
 * Who is in the session, from the host's roster.
 *
 * The roster rather than the peer list, and the difference matters: a guest holds exactly one
 * connection, so a list built from peers would show a guest one other person in a game of four. The
 * host is the only machine that can see everybody, so the host is the only machine that says who is
 * here, and everyone renders the same answer.
 *
 * A row carries only what a player acts on. Who, whether they are ready, which way their
 * connection got there and what it costs them, and whether the seat is being kept for somebody who
 * dropped. Anything more detailed, the millisecond figure behind the latency chip included, belongs
 * in the diagnostics panel.
 */

import {
  LOBBY_NOT_READY,
  LOBBY_READY,
  LOBBY_TAG_HELD,
  LOBBY_TAG_HOST,
  LOBBY_TAG_SELF,
} from '@/text/lobbyText';
import { el, setClass, setText } from '@/ui/dom';
import { bandLabel } from '@/net/lobby/latencyBand';
import { pathLabel } from '@/net/lobby/lobbyQuality';
import type { RosterSeat } from '@/net/wire/lobbyFrame';

export interface LobbyPlayerList {
  readonly root: HTMLElement;
  /** `localPlayer` is marked, so a player can find themselves in a list of four similar rows. */
  show(seats: readonly RosterSeat[], localPlayer: number | null): void;
}

export function createLobbyPlayerList(): LobbyPlayerList {
  const root = el('ul', 'lobby-members');

  return {
    root,
    show(seats: readonly RosterSeat[], localPlayer: number | null): void {
      root.replaceChildren(...seats.map((seat) => row(seat, seat.playerId === localPlayer)));
    },
  };
}

function row(seat: RosterSeat, isSelf: boolean): HTMLElement {
  const item = el('li', 'lobby-member');
  setClass(item, 'lobby-member-self', isSelf);
  setClass(item, 'lobby-member-held', seat.held);

  const name = el('span', 'lobby-member-name', seat.name);
  const tags = el('span', 'lobby-member-tags');

  if (seat.host) tags.append(tag(LOBBY_TAG_HOST, 'lobby-tag-host'));
  if (isSelf) tags.append(tag(LOBBY_TAG_SELF, 'lobby-tag-self'));

  // Nothing about the host's own readiness or connection: it is the one nobody is waiting on and
  // the one machine that is not connected to itself, so both would be answers to unasked questions.
  if (!seat.host) {
    // A held seat is neither ready nor connected, so saying either would be a second wrong answer.
    tags.append(
      seat.held
        ? tag(LOBBY_TAG_HELD, 'lobby-tag-held')
        : tag(
            seat.ready ? LOBBY_READY : LOBBY_NOT_READY,
            seat.ready ? 'lobby-tag-ready' : 'lobby-tag-wait',
          ),
    );
    if (!seat.held) {
      tags.append(tag(pathLabel(seat.quality.path), 'lobby-tag-link'));
      // No chip at all until the heartbeat has measured something. A row saying that nothing is
      // known yet is a row a player has to read and cannot act on.
      if (seat.quality.band !== 'unmeasured') {
        tags.append(tag(bandLabel(seat.quality.band), `lobby-tag-band-${seat.quality.band}`));
      }
    }
  }

  item.append(name, tags);
  return item;
}

function tag(text: string, className: string): HTMLElement {
  const chip = el('span', `lobby-tag ${className}`);
  setText(chip, text);
  return chip;
}
